import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Cookie, Settings, Globe, BarChart3, Shield, ExternalLink } from 'lucide-react';

export const CookiePolicyPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';

  if (isSpanish) {
    // VERSION ESPAGNOLE COMPLÈTE
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
        <section className="py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-sage-900 mb-4">
                Política de Cookies
              </h1>
              <div className="text-sm text-sage-500">
                Última actualización: 01 Enero 2026
              </div>
            </div>

            {/* Qué es una cookie */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-sage-600" />
                  ¿Qué es una cookie?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sage-700 leading-relaxed">
                  Las cookies son datos colocados para sus computadoras o dispositivos móviles. Se pueden distinguir por sus identificadores únicos, asociados con sus computadoras o teléfonos. Estos le permiten no tener una mala experiencia de consumo en sus sitios favoritos, ya que recuerdan sus acciones y preferencias. De esta manera, no tiene que ingresar sus preferencias de navegación cada vez que lo usa, como la ubicación, la fuente o el idioma.
                </p>
                <p className="text-sage-700 leading-relaxed">
                  Estas "cookies" se almacenan en la memoria RAM de su computadora. No nos permiten identificarlo; sin embargo, registran información relacionada con la navegación de su computadora en el Sitio (las páginas que ha visto, la fecha y hora de la consulta, etc.) que podemos leer durante sus visitas posteriores.
                </p>
              </CardContent>
            </Card>

            {/* Tipos de cookies */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>¿Cuáles son los propósitos de las cookies recopiladas?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Settings className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-sage-800">Cookies necesarias para la personalización y funcionamiento</h3>
                    <Badge className="bg-green-100 text-green-800 border-green-200">Obligatorias</Badge>
                  </div>
                  <p className="text-sage-700 text-sm mb-4">
                    Le permiten utilizar las principales funciones del sitio web y, en particular, registrar información entre dos visitas al sitio web en el mismo dispositivo, guardar credenciales de inicio de sesión o incluso elementos de personalización de la interfaz (elección del idioma o presentación). No requieren su consentimiento previo. Estas cookies son esenciales para el correcto funcionamiento del Sitio.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-sage-800">Cookies analíticas y estadísticas</h3>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">Opcionales</Badge>
                  </div>
                  <p className="text-sage-700 text-sm">
                    Nos permiten conocer el uso y el rendimiento de la audiencia del sitio web y mejorar su funcionamiento para nuestros visitantes, por ejemplo, establecer estadísticas y volúmenes de tráfico y uso de los diversos elementos que componen el sitio web (secciones y contenidos visitados, recorridos).
                  </p>
                </div>

                {/* Tableau cookies */}
                <div className="overflow-x-auto mt-6">
                  <table className="w-full border-collapse border border-sage-200">
                    <thead>
                      <tr className="bg-sage-50">
                        <th className="border border-sage-200 px-4 py-3 text-left">NOMBRE DE LA COOKIE</th>
                        <th className="border border-sage-200 px-4 py-3 text-left">PROPÓSITO</th>
                        <th className="border border-sage-200 px-4 py-3 text-left">PERÍODO DE RETENCIÓN</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-sage-200 px-4 py-3 text-sm font-mono">GCLICK</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">Cookie analítica; Seguimiento del impacto de las campañas</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">1 año</td>
                      </tr>
                      <tr>
                        <td className="border border-sage-200 px-4 py-3 text-sm font-mono">CONSENTIMIENTO DE COOKIES</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">Cookie necesaria; Gestión del consentimiento de cookies</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">6 meses</td>
                      </tr>
                      <tr>
                        <td className="border border-sage-200 px-4 py-3 text-sm font-mono">savea-language</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">Preferencia idioma usuario</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">6 meses</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Gestión cookies navegador */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>¿Cómo configuro cookies en los navegadores web?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sage-700 leading-relaxed">
                  Tiene la opción de configurar su navegador para aceptar o rechazar todas las cookies, eliminar las cookies periódicamente o ver cuándo se emite una cookie, su período de validez y su contenido, y negarse a guardarla en su disco duro.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <div>
                      <strong className="text-sage-800">En Internet Explorer:</strong>
                      <p className="text-sage-600 mt-1">
                        Abra el menú "Herramientas", luego seleccione "Opciones de Internet"; haga clic en la pestaña "Privacidad", luego en la pestaña "Avanzado", elija el nivel deseado.
                      </p>
                    </div>
                    <div>
                      <strong className="text-sage-800">En Microsoft Edge:</strong>
                      <p className="text-sage-600 mt-1">
                        Abra el menú "Herramientas", luego seleccione "Opciones de Internet"; haga clic en la pestaña "Privacidad", luego en la pestaña "Avanzado".
                      </p>
                    </div>
                    <div>
                      <strong className="text-sage-800">En Mozilla Firefox:</strong>
                      <p className="text-sage-600 mt-1">
                        Abra el menú "Herramientas", luego seleccione "Opciones"; haga clic en la pestaña "Privacidad" y luego elija las opciones deseadas.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <strong className="text-sage-800">En Safari Mac/iPhone/iPad:</strong>
                      <p className="text-sage-600 mt-1">
                        Elija "Preferencias de Safari >" y luego haga clic en "Seguridad"; En la sección "Aceptar cookies", elija las opciones deseadas.
                      </p>
                    </div>
                    <div>
                      <strong className="text-sage-800">En Google Chrome:</strong>
                      <p className="text-sage-600 mt-1">
                        Abra el menú de configuración, luego seleccione "Opciones"; haga clic en "Opciones avanzadas" y luego en "Configuración de contenido".
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // VERSION FRANÇAISE COMPLÈTE
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-sage-900 mb-4">
              Politique des Cookies
            </h1>
            <div className="text-sm text-sage-500">
              Dernière mise à jour : 01 Janvier 2026
            </div>
          </div>

          {/* Qu'est-ce qu'un cookie */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="w-5 h-5 text-sage-600" />
                Qu'est-ce qu'un cookie ?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                Les cookies sont des données placées sur vos ordinateurs ou mobiles. Ils sont différenciables par leurs identifiants uniques, associés à vos ordinateurs ou téléphones. Ces derniers vous permettent de ne pas avoir une mauvaise expérience consommateur sur vos sites favoris, puisqu'ils se souviennent de vos actions et préférences. Ainsi, vous n'avez pas besoin de renseigner vos préférences de navigation à chaque utilisation, telle que la localisation, la police ou encore la langue.
              </p>
              <p className="text-sage-700 leading-relaxed">
                Ces "cookies" sont stockées en mémoire vive dans votre ordinateur. Ils ne nous permettent pas de vous identifier ; en revanche, ils enregistrent des informations relatives à la navigation de votre ordinateur sur le Site (les pages que vous avez consultées, la date et l'heure de la consultation, etc.) que nous pourrons lire lors de vos visites ultérieures.
              </p>
            </CardContent>
          </Card>

          {/* Finalités des cookies */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Quelles sont les finalités des cookies collectés ?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Settings className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-sage-800">Les cookies nécessaires à la personnalisation et au fonctionnement du site internet</h3>
                  <Badge className="bg-green-100 text-green-800 border-green-200">Obligatoires</Badge>
                </div>
                <p className="text-sage-700 text-sm">
                  Ils vous permettent d'utiliser les principales fonctionnalités du Site et notamment d'enregistrer des informations entre deux consultations du site sur un même appareil, d'enregistrer des identifiants de connexion de session ou encore des éléments de personnalisation de l'interface (choix de la langue ou présentation). Ils ne requièrent pas votre consentement préalable. Ces cookies sont indispensables au bon fonctionnement du Site.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-sage-800">Les cookies analytiques, de statistique</h3>
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">Optionnels</Badge>
                </div>
                <p className="text-sage-700 text-sm">
                  Cookies de mesure d'audience du site internet qui nous permettent de connaître l'utilisation et les performances d'audience du site et d'en améliorer le fonctionnement pour nos visiteurs, par exemple, établir des statistiques et volumes de fréquentation et d'utilisation des divers éléments composant le site (rubriques et contenus visités, parcours), afin d'améliorer l'intérêt et l'ergonomie du site.
                </p>
              </div>

              {/* Tableau des cookies */}
              <div className="mt-6">
                <h4 className="font-semibold text-sage-800 mb-4">Détail des cookies utilisés :</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-sage-200">
                    <thead>
                      <tr className="bg-sage-50">
                        <th className="border border-sage-200 px-4 py-3 text-left">NOM DU COOKIE</th>
                        <th className="border border-sage-200 px-4 py-3 text-left">FINALITÉ</th>
                        <th className="border border-sage-200 px-4 py-3 text-left">DURÉE DE CONSERVATION</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-sage-200 px-4 py-3 text-sm font-mono">GCLICK</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">Cookie analytique ; suivi de l'impact des campagnes</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">1 an</td>
                      </tr>
                      <tr>
                        <td className="border border-sage-200 px-4 py-3 text-sm font-mono">COOKIE CONSENTEMENT</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">Cookie nécessaire ; Gestion du consentement des cookies</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">6 mois</td>
                      </tr>
                      <tr>
                        <td className="border border-sage-200 px-4 py-3 text-sm font-mono">savea-language</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">Préférence langue utilisateur</td>
                        <td className="border border-sage-200 px-4 py-3 text-sm">6 mois</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Paramétrage navigateurs */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Comment paramétrer les cookies sur les navigateurs web ?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sage-700 leading-relaxed">
                Vous avez le choix de configurer votre navigateur pour accepter ou refuser tous les cookies, supprimer les cookies périodiquement ou encore de voir quand un cookie est émis, sa durée de validité, et son contenu, et refuser son enregistrement sur votre disque dur.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div className="space-y-4">
                  <div>
                    <strong className="text-sage-800">Sur Internet Explorer :</strong>
                    <p className="text-sage-600 mt-1">
                      Ouvrez le menu "Outils", puis sélectionnez "Options internet" ; cliquez sur l'onglet "Confidentialité" puis l'onglet "Avancé" choisissez le niveau souhaité.
                    </p>
                    <a href="https://support.microsoft.com/fr-fr/help/17442/windows-internet-explorer-delete-manage-cookies" 
                       target="_blank" 
                       className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-2">
                      <ExternalLink className="w-3 h-3" />
                      Guide Microsoft
                    </a>
                  </div>

                  <div>
                    <strong className="text-sage-800">Sur Mozilla Firefox :</strong>
                    <p className="text-sage-600 mt-1">
                      Ouvrez le menu "Outils", puis sélectionnez "Options" ; cliquez sur l'onglet "Vie privée" puis choisissez les options souhaitées.
                    </p>
                    <a href="https://support.mozilla.org/fr/products/firefox/protect-your-privacy/cookies" 
                       target="_blank" 
                       className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-2">
                      <ExternalLink className="w-3 h-3" />
                      Guide Mozilla
                    </a>
                  </div>

                  <div>
                    <strong className="text-sage-800">Sur Safari Mac/iPhone/iPad :</strong>
                    <p className="text-sage-600 mt-1">
                      Choisissez "Safari > Préférences" puis cliquez sur "Sécurité" ; Dans la section "Accepter les cookies" choisissez les options souhaitées.
                    </p>
                    <a href="https://support.apple.com/fr-fr/HT201265" 
                       target="_blank" 
                       className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-2">
                      <ExternalLink className="w-3 h-3" />
                      Guide Apple
                    </a>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <strong className="text-sage-800">Sur Google Chrome :</strong>
                    <p className="text-sage-600 mt-1">
                      Ouvrez le menu de configuration, puis sélectionnez "Options" ; cliquez sur "Options avancées" puis dans la section "Confidentialité", cliquez sur "Paramètres de contenu".
                    </p>
                    <a href="https://support.google.com/chrome/answer/95647?hl=fr" 
                       target="_blank" 
                       className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-2">
                      <ExternalLink className="w-3 h-3" />
                      Guide Google
                    </a>
                  </div>

                  <div>
                    <strong className="text-sage-800">Pour plus d'informations :</strong>
                    <p className="text-sage-600 mt-1">
                      Vous pouvez également taper "cookies" dans la rubrique "aide" de votre navigateur afin d'accéder aux instructions de paramétrage.
                    </p>
                    <a href="https://www.cnil.fr/cnil-direct/question/198" 
                       target="_blank" 
                       className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-2">
                      <ExternalLink className="w-3 h-3" />
                      Site de la CNIL
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};